import { ORPCError } from "@orpc/client";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { retryUnlessNotFound } from "@packages/core-client/query/retryUnlessNotFound.ts";
import { useLoadingIndicator } from "@packages/core-client/hooks/useLoadingIndicator.tsx";
import { RoutedBreadcrumb } from "@packages/core-client/routing/RoutedBreadcrumb.tsx";
import { useI18nextInstance } from "@squide/i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMatches } from "react-router";
import { toast } from "sonner";
import { useDeactivatePersonMutationOptions, useManagePeopleClient, useReactivatePersonMutationOptions, useRemovePersonMutationOptions, useResendInviteMutationOptions, useSendInviteMutationOptions } from "../../../api/queries/managePeople.ts";
import { i18NextInstanceKey } from "../../../i18n/i18next.ts";
import { DeactivatePersonDialog } from "../DeactivatePersonDialog.tsx";
import { isSelfIncluded } from "../manageSelfGuard.ts";
import { RemovePersonDialog } from "../RemovePersonDialog.tsx";
import { resolveInviteErrorToastKey } from "../resolveInviteErrorToastKey.ts";
import type { PersonHeaderAction } from "./personDetailHeaderActions.ts";
import { PersonDetailHeader } from "./PersonDetailHeader.tsx";
import { PLACEHOLDER_PERSON } from "./personDetailPlaceholder.ts";
import { useSkipUnsavedChangesGuardNavigate } from "../../../hooks/useUnsavedChangesBlocker.ts";

export function PersonDetailTabsHeading() {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("PersonDetailTabsHeading", { i18n: i18nextInstance });
    const { t: tShared } = useTranslation("managePeople", { i18n: i18nextInstance });

    const matches = useMatches();
    const personId = matches[matches.length - 1]?.params?.personId ?? "";

    const { queryUtils } = useManagePeopleClient();
    const queryClient = useQueryClient();
    const navigate = useSkipUnsavedChangesGuardNavigate();
    const session = useSession();

    const { data, isPending } = useQuery(
        createQueryOptions(queryUtils.getPerson.queryOptions({ input: { personId } }), {
            throwOnError: false,
            retry: retryUnlessNotFound
        })
    );

    const isLoading = useLoadingIndicator(isPending);
    const hasContent = isLoading || Boolean(data);
    const person = isLoading ? PLACEHOLDER_PERSON : (data ?? PLACEHOLDER_PERSON);

    const deactivatePersonMutation = useMutation(useDeactivatePersonMutationOptions());
    const reactivatePersonMutation = useMutation(useReactivatePersonMutationOptions());
    const sendInviteMutation = useMutation(useSendInviteMutationOptions());
    const resendInviteMutation = useMutation(useResendInviteMutationOptions());
    const removePersonMutation = useMutation(useRemovePersonMutationOptions());

    const [isDeactivateDialogOpen, setIsDeactivateDialogOpen] = useState(false);
    const handleCloseDeactivateDialog = useCallback(() => setIsDeactivateDialogOpen(false), []);
    const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
    const handleCloseRemoveDialog = useCallback(() => setIsRemoveDialogOpen(false), []);

    const getPersonQueryKey = useMemo(() => queryUtils.getPerson.key({ input: { personId } }), [queryUtils, personId]);

    const handleMutationError = useCallback(
        (error: unknown, toastKey: string) => {
            if (error instanceof ORPCError && error.code === "NOT_FOUND") {
                void queryClient.invalidateQueries({ queryKey: getPersonQueryKey });
            }

            toast.error(tShared(`toasts.${toastKey}`), { position: "top-right" });
        },
        [queryClient, getPersonQueryKey, tShared]
    );

    const handleAction = useCallback(
        (action: PersonHeaderAction) => {
            const input = { personId };

            switch (action) {
                case "deactivate":
                    setIsDeactivateDialogOpen(true);
                    return;
                case "remove":
                    setIsRemoveDialogOpen(true);
                    return;
                case "reactivate":
                    reactivatePersonMutation.mutate(input, {
                        onSuccess: () => toast.success(tShared("toasts.reactivatedToast"), { position: "top-right" }),
                        onError: error => handleMutationError(error, "reactivateErrorToast")
                    });
                    return;
                case "sendInvite":
                    sendInviteMutation.mutate(input, {
                        onSuccess: () => toast.success(tShared("toasts.inviteSentToast"), { position: "top-right" }),
                        onError: error => handleMutationError(error, resolveInviteErrorToastKey(error, "sendInviteErrorToast"))
                    });
                    return;
                case "resendInvite":
                    resendInviteMutation.mutate(input, {
                        onSuccess: () => toast.success(tShared("toasts.inviteResentToast"), { position: "top-right" }),
                        onError: error => handleMutationError(error, resolveInviteErrorToastKey(error, "resendInviteErrorToast"))
                    });
                    return;
            }
        },
        [personId, reactivatePersonMutation, sendInviteMutation, resendInviteMutation, tShared, handleMutationError]
    );

    const handleConfirmDeactivate = useCallback(() => {
        deactivatePersonMutation.mutate(
            { personId },
            {
                onSuccess: () => {
                    setIsDeactivateDialogOpen(false);
                    toast.success(tShared("toasts.deactivatedToast"), { position: "top-right" });
                },
                onError: error => handleMutationError(error, "deactivateErrorToast")
            }
        );
    }, [deactivatePersonMutation, personId, tShared, handleMutationError]);

    const handleConfirmRemove = useCallback(async () => {
        try {
            await removePersonMutation.mutateAsync({ personId });
        } catch (error) {
            handleMutationError(error, "removeErrorToast");

            return;
        }

        setIsRemoveDialogOpen(false);
        navigate("/settings/people");
        toast.success(tShared("toasts.removedToast"), { position: "top-right" });
    }, [removePersonMutation, personId, navigate, tShared, handleMutationError]);

    const pendingAction: PersonHeaderAction | undefined = deactivatePersonMutation.isPending
        ? "deactivate"
        : reactivatePersonMutation.isPending
          ? "reactivate"
          : sendInviteMutation.isPending
            ? "sendInvite"
            : resendInviteMutation.isPending
              ? "resendInvite"
              : removePersonMutation.isPending
                ? "remove"
                : undefined;

    return (
        <div className="-mt-4 w-full">
            <output className="sr-only">{isLoading && t("liveRegion.loading")}</output>

            {hasContent && (
                <Skeleton isLoading={isLoading} inert={isLoading}>
                    <div className="flex flex-col gap-6">
                        <RoutedBreadcrumb ariaLabel={t("breadcrumb.ariaLabel")} items={[{ label: t("breadcrumb.managePeople"), href: "/settings/people" }, { label: person.displayName }]} />

                        <PersonDetailHeader person={person} isSelf={isSelfIncluded(person, session?.memberId)} pendingAction={pendingAction} onAction={handleAction} />
                    </div>
                </Skeleton>
            )}

            {isDeactivateDialogOpen && <DeactivatePersonDialog isOpen onOpenChange={handleCloseDeactivateDialog} onConfirm={handleConfirmDeactivate} isDeactivating={deactivatePersonMutation.isPending} />}

            {isRemoveDialogOpen && <RemovePersonDialog isOpen onOpenChange={handleCloseRemoveDialog} onConfirm={handleConfirmRemove} isRemoving={removePersonMutation.isPending} />}
        </div>
    );
}
